import * as Sentry from '@sentry/react'

let isInitialized = false

export const initializeSentry = async () => {
  if (isInitialized) {
    console.info('Sentry already initialized')
    return
  }

  // NB: we no longer bail out on Brave. Brave's shields may block the transport
  // for some users, but the SDK handles a blocked transport gracefully — and
  // skipping init entirely meant every Brave user's crashes went unreported.

  const dsn = import.meta.env.VITE_SENTRY_DSN || ''
  console.log('Sentry DSN check:', dsn ? 'DSN configured' : 'DSN missing')

  if (!dsn) {
    console.warn(
      'Sentry DSN not configured - add SENTRY_REACT_DSN to your .env file for clearworks-react project'
    )
    return
  }

  const environment =
    window.location.host === 'smilebar.clearworks-staging.xyz'
      ? 'staging'
      : import.meta.env.VITE_NODE_ENV || 'development'

  try {
    Sentry.init({
      dsn,
      environment,
      integrations: [
        new Sentry.BrowserTracing({
          tracePropagationTargets: [
            'localhost',
            /^https:\/\/.*\.clearworks.*\.xyz/,
          ],
        }),
        new Sentry.Replay({
          maskAllText: true,
          blockAllMedia: true,
          networkDetailAllowUrls: [window.location.origin],
          networkCaptureBodies: true,
          networkRequestHeaders: ['content-type'],
          networkResponseHeaders: ['content-type'],
        }),
      ],
      tracesSampleRate: environment === 'production' ? 0.1 : 0.5,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      release: import.meta.env.VITE_APP_VERSION || 'development',
      // NB: ChunkLoadError / "Loading chunk" are intentionally NOT ignored.
      // They fire when a client holds a stale index.html after a deploy and the
      // hashed asset it asks for is gone — which surfaces to the user as the
      // "Oops! Something went wrong" crash that "fixes itself" on reload. We
      // want that signal, not silence. Only genuine, non-actionable noise is
      // filtered here.
      ignoreErrors: [
        'NotAllowedError',
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        'NetworkError',
        'Failed to fetch',
        'Load failed',
        'chrome-extension://',
        'moz-extension://',
      ],
      denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
      beforeSend(event, hint) {
        if (
          environment === 'development' &&
          !import.meta.env.VITE_SENTRY_DEBUG
        ) {
          console.log('Sentry event (not sent in dev):', event)
          return null
        }

        const error = hint.originalException
        if (error instanceof Error) {
          if (
            error.message === 'Script error.' ||
            error.message === '' ||
            !error.stack
          ) {
            return null
          }
        }

        return event
      },
      beforeBreadcrumb(breadcrumb, hint) {
        if (breadcrumb.category === 'ui.click') {
          const target = hint?.event?.target
          if (target) {
            breadcrumb.message = `${breadcrumb.message} - ${target.tagName}`
            if (target.id)
              breadcrumb.data = { ...breadcrumb.data, id: target.id }
            if (target.className)
              breadcrumb.data = {
                ...breadcrumb.data,
                className: target.className,
              }
          }
        }
        return breadcrumb
      },
      initialScope: {
        tags: {
          app_section: 'frontend',
          project: 'clearworks-react',
        },
      },
    })

    isInitialized = true
    console.info(`Sentry initialized for ${environment} environment`)
  } catch (error) {
    console.error('Failed to initialize Sentry:', error)
  }
}

export const setSentryUser = user => {
  if (!isInitialized || !user) return

  Sentry.setUser({
    id: user.id?.toString(),
    email: user.email,
    username: user.name || user.username,
    role: user.role,
    organization: user.organization_name,
  })
}

export const clearSentryUser = () => {
  if (!isInitialized) return
  Sentry.setUser(null)
}

export const setSentryContext = (key, data) => {
  if (!isInitialized) return
  Sentry.setContext(key, data)
}

export const addSentryBreadcrumb = (message, data = {}, level = 'info') => {
  if (!isInitialized) return

  Sentry.addBreadcrumb({
    message,
    level,
    data,
    timestamp: Date.now() / 1000,
  })
}

export const captureSentryException = (error, context = {}) => {
  if (!isInitialized) return

  Sentry.withScope(scope => {
    Object.entries(context).forEach(([key, value]) => {
      if (typeof value === 'object') {
        scope.setContext(key, value)
      } else {
        scope.setTag(key, value)
      }
    })

    Sentry.captureException(error)
  })
}

export const captureSentryMessage = (message, level = 'info') => {
  if (!isInitialized) return
  Sentry.captureMessage(message, level)
}

export default Sentry
